import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BoltIcon, SignalIcon, ShieldCheckIcon, ClockIcon, WifiIcon, ChartBarIcon } from '@heroicons/react/24/outline';

const videoSequence = ['/videos/intro.mp4', '/videos/1.mp4', '/videos/2.mp4', '/videos/3.mp4', '/videos/4.mp4'];

const Gamers: React.FC = () => {
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activePlayer, setActivePlayer] = useState<'A' | 'B'>('A');
  const [isMuted, setIsMuted] = useState(true);
  const loopCount = useRef(0);

  const toggleMute = () => {
    const videoA = videoARef.current;
    const videoB = videoBRef.current;
    if (videoA && videoB) {
      const newMuted = !isMuted;
      videoA.muted = newMuted;
      videoB.muted = newMuted;
      setIsMuted(newMuted);
    }
  };

  // Auto-unmute on any user interaction with the page
  useEffect(() => {
    const handleFirstInteraction = () => {
      const videoA = videoARef.current;
      const videoB = videoBRef.current;
      if (videoA) videoA.muted = false;
      if (videoB) videoB.muted = false;
      setIsMuted(false);
      window.removeEventListener('click', handleFirstInteraction, true);
      window.removeEventListener('keydown', handleFirstInteraction, true);
      window.removeEventListener('touchstart', handleFirstInteraction, true);
      window.removeEventListener('scroll', handleFirstInteraction, true);
    };
    window.addEventListener('click', handleFirstInteraction, true);
    window.addEventListener('keydown', handleFirstInteraction, true);
    window.addEventListener('touchstart', handleFirstInteraction, true);
    window.addEventListener('scroll', handleFirstInteraction, true);
    return () => {
      window.removeEventListener('click', handleFirstInteraction, true);
      window.removeEventListener('keydown', handleFirstInteraction, true);
      window.removeEventListener('touchstart', handleFirstInteraction, true);
      window.removeEventListener('scroll', handleFirstInteraction, true);
    };
  }, []);

  // Preload next video into the inactive player
  useEffect(() => {
    const nextIndex = (currentIndex + 1) % videoSequence.length;
    const inactiveVideo = activePlayer === 'A' ? videoBRef.current : videoARef.current;
    if (inactiveVideo) {
      inactiveVideo.src = videoSequence[nextIndex];
      inactiveVideo.load();
    }
  }, [currentIndex, activePlayer]);

  // Switch to next video
  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % videoSequence.length;
    // If looping back to intro, that means first loop is done — mute
    if (nextIndex === 0) {
      loopCount.current += 1;
      if (loopCount.current >= 1) {
        const videoA = videoARef.current;
        const videoB = videoBRef.current;
        if (videoA) videoA.muted = true;
        if (videoB) videoB.muted = true;
        setIsMuted(true);
      }
    }
    const nextPlayer = activePlayer === 'A' ? 'B' : 'A';
    const nextVideo = activePlayer === 'A' ? videoBRef.current : videoARef.current;
    if (nextVideo) {
      nextVideo.muted = loopCount.current >= 1 ? true : isMuted;
      nextVideo.play().catch(() => {});
    }
    setActivePlayer(nextPlayer);
    setCurrentIndex(nextIndex);
  };

  // Play current video, cut intro at 7s, others play to end
  useEffect(() => {
    const activeVideo = activePlayer === 'A' ? videoARef.current : videoBRef.current;
    if (!activeVideo) return;

    if (!activeVideo.src || !activeVideo.src.endsWith(videoSequence[currentIndex])) {
      activeVideo.src = videoSequence[currentIndex];
      activeVideo.load();
    }
    activeVideo.muted = isMuted;
    activeVideo.play().catch(() => {});

    const isIntro = currentIndex === 0;

    const handleTimeUpdate = () => {
      if (isIntro && activeVideo.currentTime >= 7) {
        activeVideo.removeEventListener('timeupdate', handleTimeUpdate);
        goToNext();
      }
    };

    const handleEnded = () => {
      goToNext();
    };

    if (isIntro) {
      activeVideo.addEventListener('timeupdate', handleTimeUpdate);
    }
    activeVideo.addEventListener('ended', handleEnded);

    return () => {
      activeVideo.removeEventListener('timeupdate', handleTimeUpdate);
      activeVideo.removeEventListener('ended', handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, activePlayer, isMuted]);

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white pt-20">

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 bg-gradient-to-br from-airmax-blue to-blue-700 text-white">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.2) 1px, transparent 0)',
          backgroundSize: '30px 30px'
        }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <span className="inline-block bg-white/20 border border-white/30 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                Built for Gamers
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight text-white">
                Wave Fiber Speeds.<br />
                <span className="text-blue-100">Built for Gamers.</span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-8 max-w-lg">
                Experience Wave Fiber speeds with a dedicated connection that doesn't share bandwidth with your neighborhood. Lower ping, faster downloads, and the consistent performance gamers need to stay ahead.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/"
                  className="bg-white text-airmax-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all text-center"
                >
                  Check Availability
                </Link>
                <Link
                  to="/plans#wave-fiber"
                  className="border-2 border-white/50 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-all text-center"
                >
                  View Wave Fiber Plans
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center lg:col-span-3"
            >
              <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-white/20 relative bg-black">
                <video
                  ref={videoARef}
                  className="w-full block absolute inset-0"
                  muted
                  playsInline
                  preload="auto"
                  controls={false}
                  style={{
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                    opacity: activePlayer === 'A' ? 1 : 0,
                    transition: 'opacity 0.05s ease-in-out'
                  }}
                />
                <video
                  ref={videoBRef}
                  className="w-full block"
                  muted
                  playsInline
                  preload="auto"
                  controls={false}
                  style={{
                    objectFit: 'cover',
                    width: '100%',
                    opacity: activePlayer === 'B' ? 1 : 0,
                    transition: 'opacity 0.05s ease-in-out'
                  }}
                />
                <button
                  onClick={toggleMute}
                  className="absolute bottom-3 right-3 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors z-10"
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728" />
                    </svg>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-white/10 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {[
              { value: 'Low', label: 'Latency' },
              { value: 'Up to 1 Gbps', label: 'Download Speed' },
              { value: 'Dedicated', label: 'Connection' },
              { value: 'Minimal', label: 'Packet Loss' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="py-8 text-center"
              >
                <p className="text-2xl md:text-3xl font-extrabold text-blue-400">{stat.value}</p>
                <p className="text-sm text-white/50 mt-1 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Gamers Choose Airmax */}
      <section className="py-16 md:py-24 bg-[#0a0f1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Gamers Choose <span className="text-blue-400">Airmax</span></h2>
            <p className="text-white/60 max-w-2xl mx-auto text-lg">
              Cable and DSL share bandwidth with your entire neighborhood. Airmax gives you a dedicated connection — so your ping stays low even during peak hours.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ClockIcon,
                title: 'Low Latency',
                desc: 'A dedicated connection means less congestion and lower ping. Your inputs get to the server faster so you can react quicker in competitive matches.'
              },
              {
                icon: BoltIcon,
                title: 'Fast Speeds',
                desc: 'Download games faster and stream in HD while someone else games. With speeds up to 1 Gbps, your household has bandwidth to spare.'
              },
              {
                icon: SignalIcon,
                title: 'Dedicated Connection',
                desc: "Unlike cable internet, your Airmax connection isn't shared with neighbors. Your speed stays consistent — no slowdowns during peak hours."
              },
              {
                icon: ShieldCheckIcon,
                title: 'Reliable Network',
                desc: 'Our network is built for stability. Less packet loss means less rubber-banding and fewer disconnects during your gaming sessions.'
              },
              {
                icon: ChartBarIcon,
                title: 'Consistent Performance',
                desc: "No more speed drops during prime time. While cable users fight for bandwidth at 8 PM, your connection stays steady."
              },
              {
                icon: WifiIcon,
                title: 'Whole Home Coverage',
                desc: 'Game from any room. Our setup provides strong WiFi throughout your entire home — bedroom, living room, or gaming den.'
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="bg-[#111827] border border-white/10 rounded-xl p-6 hover:border-blue-500/40 hover:shadow-lg hover:shadow-black/30 transition-all group"
              >
                <div className="w-12 h-12 bg-blue-500/15 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-500/25 transition-colors">
                  <item.icon className="h-6 w-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Airmax vs Cable Comparison */}
      <section className="py-16 md:py-24 bg-[#111827]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Airmax vs. Cable Internet</h2>
            <p className="text-white/60 text-lg">See why gamers are making the switch</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="overflow-hidden rounded-xl border border-white/10 shadow-lg shadow-black/30"
          >
            <table className="w-full">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="text-left py-4 px-6 font-medium text-sm">Feature</th>
                  <th className="text-center py-4 px-6">
                    <span className="font-bold">Airmax</span>
                  </th>
                  <th className="text-center py-4 px-6 font-medium text-sm">Cable / DSL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {[
                  { feature: 'Latency', airmax: 'Low', cable: 'Variable (15-80ms)' },
                  { feature: 'Download Speed', airmax: 'Up to 1 Gbps', cable: '100-300 Mbps typical' },
                  { feature: 'Connection Type', airmax: 'Dedicated', cable: 'Shared' },
                  { feature: 'Peak Hour Performance', airmax: 'Consistent', cable: 'Often degrades' },
                  { feature: 'Packet Loss', airmax: 'Minimal', cable: 'Can spike during congestion' },
                  { feature: 'Setup Time', airmax: '1-2 Days', cable: '1-3 Weeks' },
                  { feature: 'Contract Required', airmax: 'No', cable: 'Usually Yes' },
                ].map((row, i) => (
                  <tr key={i} className="bg-[#0a0f1a] hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6 text-white/80 text-sm font-medium">{row.feature}</td>
                    <td className="py-4 px-6 text-center text-green-400 font-semibold text-sm">{row.airmax}</td>
                    <td className="py-4 px-6 text-center text-white/40 text-sm">{row.cable}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Testimonial / Quote Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-airmax-blue to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="text-2xl md:text-3xl font-bold text-white italic leading-relaxed mb-6">
              "I switched from cable to Airmax and my gaming experience improved right away. My ping dropped and I stopped getting lag spikes during peak hours. Huge difference."
            </p>
            <p className="text-blue-200 font-semibold">— Airmax Customer, Arizona</p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#0a0f1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Level Up Your Connection?</h2>
            <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
              Stop fighting lag and slow speeds. Check if Airmax is available at your address and start gaming the way it should be.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-500 transition-all"
              >
                Check Availability
              </Link>
              <Link
                to="/plans#wave-fiber"
                className="border-2 border-blue-400 text-blue-300 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-500/10 transition-all"
              >
                View Wave Fiber Plans
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Gamers;
